import { TalentManagerEngagementStatus } from '@talon/web/lib/api';
import { useProjectStore, useTalentManagerStore, useUserStore } from '@talon/web/src/context';
import { homeRouteFor, isTalentManagerUser } from '@talon/web/src/utils/userRoles';
import { Anchor, Box, Button, Layer, Nav, ResponsiveContext, Text } from 'grommet';
import { Close, Home, Menu as MenuIcon, UserExpert } from 'grommet-icons';
import TalonLogo from 'jsx:@talon/web/public/images/talon-ai-logo.svg';
import { observer } from 'mobx-react-lite';
import { ReactNode, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AgencyMenus, AnchorLink, ProjectSelector, useIsActivePath } from '.';
import { NotificationsMenu } from '../ui';
import { QuickEstimatorMenu } from '../ui/quick-estimator/QuickEstimatorMenu';
import { UserMenu } from './UserMenu';

/** A small pill in the header bar, e.g. "Working for Acme" or "View only" */
const NavChip = ({ children, warning }: { children: ReactNode; warning?: boolean }) => (
  <Box
    flex={false}
    round="999px"
    pad={{ horizontal: '10px', vertical: '2px' }}
    background={warning ? 'pill-warning-bg' : 'white'}
    alignSelf="center"
  >
    <Text size="12px" weight={600} color={warning ? 'pill-warning-text' : 'brand'} style={{ whiteSpace: 'nowrap' }}>
      {children}
    </Text>
  </Box>
);

export const HeaderNav = observer(() => {
  /** Context **/
  const userStore = useUserStore();
  const projectStore = useProjectStore();
  const tmStore = useTalentManagerStore();
  const navigate = useNavigate();
  const size = useContext(ResponsiveContext);
  const isSmall = size === 'small';

  /** State **/
  const [menuOpen, setMenuOpen] = useState(false);

  // A talent manager's nav depends on whether an agency has engaged them (and on what terms)
  const isTalentManager = isTalentManagerUser(userStore.user);
  useEffect(() => {
    if (isTalentManager) tmStore.fetchMyProfile();
  }, [isTalentManager, tmStore]);
  const activeEngagements = isTalentManager
    ? tmStore.myEngagements.filter((e) => e.status === TalentManagerEngagementStatus.ACTIVE)
    : [];
  const currentEngagement = activeEngagements.find((e) => e.project_id === projectStore.currentProject?.id);

  const homeRoute = homeRouteFor(userStore.user);

  const logo = (
    <Anchor as={Button} onClick={() => navigate(homeRoute)}>
      <Box direction="row" gap="xsmall" align="center">
        <Box justify="center">
          <TalonLogo width="36" height="36" />
        </Box>
        <Box margin={{ bottom: '2px' }} direction="row" align="center">
          <Text weight={400} style={{ letterSpacing: '0.2rem' }}>
            TALON
          </Text>
        </Box>
      </Box>
    </Anchor>
  );

  // Everything below the logo that isn't a single icon button — the project/agency mega-menu,
  // the performer's and talent manager's own nav links, the quick estimator, and the user menu.
  // On a full-width header these sit in a row; at phone width they'd overlap into an illegible
  // pile (the header used to do exactly that), so on `small` they're stacked in a slide-out
  // drawer instead.
  // accent-1 (white) is meant for the blue header bar — on the drawer's light background it
  // would be invisible, so these get a dark color explicitly when rendered there.
  const linkColor = isSmall ? 'text' : 'accent-1';
  const dashboardActive = useIsActivePath('/talent-manager');
  const commercialsActive = useIsActivePath('/performer-home');
  const navLinks = (
    <>
      {isTalentManager && (
        <AnchorLink
          to={'/talent-manager'}
          label={
            <Text color={linkColor} weight={dashboardActive ? 600 : undefined}>
              My Dashboard
            </Text>
          }
          icon={<UserExpert size="20" color={linkColor} />}
          alignSelf="center"
        />
      )}
      {/* An agency's nav; for a talent manager, only once an agency engages them, and without Marketplace */}
      {userStore.user?.id && !userStore.user?.performer_id && (!isTalentManager || activeEngagements.length > 0) && (
        <Box direction={isSmall ? 'column' : 'row'} gap="medium" align={isSmall ? 'start' : 'center'}>
          {isTalentManager && !isSmall && (
            <Box
              width="1px"
              height="24px"
              flex={false}
              margin={{ left: 'medium' }}
              style={{ background: 'rgba(255, 255, 255, 0.35)' }}
            />
          )}
          <ProjectSelector textColor={linkColor} engagements={isTalentManager ? activeEngagements : undefined} />
          {currentEngagement && <NavChip>Working for {currentEngagement.group?.name}</NavChip>}
          {currentEngagement && !currentEngagement.can_edit_project && <NavChip warning>View only</NavChip>}
          {/* A talent manager's bar also holds their dashboard and the chips, so its global links fold into More */}
          <AgencyMenus
            textColor={linkColor}
            iconColor={linkColor}
            showMarketplace={!isTalentManager}
            foldLinks={isTalentManager}
          />
        </Box>
      )}
      {userStore.user?.performer_id && (
        <AnchorLink
          to={'/performer-home'}
          label={
            <Text color={linkColor} weight={commercialsActive ? 600 : undefined}>
              My Commercials
            </Text>
          }
          icon={<Home size="20" color={linkColor} />}
          alignSelf="center"
        />
      )}
    </>
  );

  if (isSmall) {
    return (
      <Nav
        direction="row"
        background={{ color: 'brand-gradient', dark: true }}
        style={{ background: 'var(--brand-gradient)', zIndex: 2 }}
        fill="horizontal"
        height="72px"
        justify="between"
        pad={{ horizontal: 'small' }}
        align="center"
        elevation="small"
      >
        {logo}
        <Box direction="row" gap="small" align="center">
          {userStore.user?.id && <NotificationsMenu />}
          <Button
            icon={<MenuIcon color="white" />}
            onClick={() => setMenuOpen(true)}
            a11yTitle="Open menu"
            plain
          />
        </Box>
        {menuOpen && (
          <Layer position="right" full="vertical" onEsc={() => setMenuOpen(false)} onClickOutside={() => setMenuOpen(false)}>
            <Box width="min(85vw, 340px)" height="100%" background="light-1" pad="medium" gap="medium" overflow="auto">
              <Box direction="row" justify="end">
                <Button icon={<Close />} onClick={() => setMenuOpen(false)} a11yTitle="Close menu" plain />
              </Box>
              <Box gap="medium">{navLinks}</Box>
              {userStore.user?.active_group && (
                // QuickEstimatorMenu's icon is hardcoded white for the blue header bar — give it
                // a matching chip here instead, rather than reworking a shared component's colors.
                <Box background="brand" round="small" width="40px" height="40px" align="center" justify="center">
                  <QuickEstimatorMenu />
                </Box>
              )}
              <UserMenu textColor="text" />
            </Box>
          </Layer>
        )}
      </Nav>
    );
  }

  /** Render **/
  return (
    <Nav
      direction="row"
      background={{ color: 'brand-gradient', dark: true }}
      style={{ background: 'var(--brand-gradient)', zIndex: 2 }}
      fill="horizontal"
      flex="grow"
      height="72px"
      justify="between"
      pad={{ horizontal: 'medium' }}
      align="center"
      elevation="small"
    >
      <Box width="auto" flex={false}>
        {logo}
      </Box>

      <Box flex />
      {navLinks}
      <Box flex />
      <Box direction="row" gap="small" justify="end" align="center" flex={false}>
        {userStore.user?.active_group && <QuickEstimatorMenu />}
        {userStore.user?.id && <NotificationsMenu />}
        <UserMenu />
      </Box>
    </Nav>
  );
});
